import React, { useContext } from 'react';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Image from 'next/image';
import { AI_WORKBENCH_OUTPUT_DETAILS, AiWorkBenchOutputEnum } from '../../../utils';
import { mergeSx, Styles } from '@t2c/shared-fe';
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';
import { Icon } from '@iconify/react';

interface Props {
  outputType: AiWorkBenchOutputEnum;
  onOutputTypeChange: (event: SelectChangeEvent<AiWorkBenchOutputEnum>) => void;
}

export const OutputSection: React.FC<Props> = ({ outputType, onOutputTypeChange }) => {
  const { userPlanData, agentPlanData } = useContext(CreditContext);
  const currentOutputDetails = AI_WORKBENCH_OUTPUT_DETAILS[outputType];

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const freeTrialAccess = userPlanData === 'freeTrialAccess' || agentPlanData === 'agentTrialPlan';
  const noActivePlan = userPlanData === 'noActivePlan' && agentPlanData === 'noAgentPlan';

  return (
    <Card
      sx={{
        ...styles.card,
        minHeight: isSmall ? 100 : 600,
        border: isExtraSmall ? 'none' : '2px solid',
        p: isExtraSmall ? 0 : 2,
      }}
    >
      <Typography variant="h6">Output</Typography>
      <Divider
        sx={{
          my: 1.5,
          borderColor: (theme) => theme.palette.background.ternary,
        }}
      />
      <Stack gap={2}>
        <FormControl fullWidth sx={styles.selectForm}>
          <Select
            labelId="output-select-label"
            value={outputType}
            onChange={(event) => {
              onOutputTypeChange(event);
            }}
            sx={styles.select}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: (theme) => theme.palette.background.secondary,
                  '& .MuiMenuItem-root': {
                    backgroundColor: (theme) => theme.palette.background.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.background.activeCard,
                    },
                  },
                  border: '1px solid #505255',
                },
              },
            }}
          >
            {Object.values(AiWorkBenchOutputEnum).map((output) => (
              <MenuItem key={output} value={output} sx={{ display: 'flex', gap: 0.5 }}>
                {output}{' '}
                {((freeTrialAccess && output !== AiWorkBenchOutputEnum.IMAGE) || noActivePlan) && (
                  <Tooltip title={'Upgrade To A Paid Account For All Feature Access'}>
                    <Icon
                      icon="mdi:lock-outline"
                      width={'17'}
                      height={'17'}
                      style={{ marginLeft: '4px' }}
                    />
                  </Tooltip>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!isSmall && (
          <Box sx={styles.exampleBox}>
            <Typography sx={styles.promptExamples}>{currentOutputDetails.message}</Typography>
          </Box>
        )}
      </Stack>
      {!isSmall && (
        <Box sx={mergeSx(styles.illustrationBox, currentOutputDetails?.style)}>
          <Image
            src={currentOutputDetails.illustration}
            alt={`${outputType} illustration`}
            fill
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
    </Card>
  );
};

const styles: Styles = {
  card: {
    p: 2,
    height: '100%',
    border: '2px solid',
    position: 'relative',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
    minHeight: 600,
  },
  select: {
    '& .MuiSelect-icon': {
      fontSize: '16px',
    },
    background: (theme) => theme.palette.background.secondary,
  },
  selectForm: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
  },
  exampleBox: {
    p: 2,
    backgroundColor: (theme) => theme.palette.background.activeCard,
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  promptExamples: {
    fontSize: '16px',
    color: (theme) => theme.palette.text.primary,
    textAlign: 'left',
  },
  illustrationBox: {
    textAlign: 'center',
    position: 'absolute',
    right: 0,
    bottom: 0,
  },
  heading: {
    fontWeight: 600,
    fontSize: '24px',
  },
};
